import React, { useContext, useRef, useState } from 'react'
import { usePopper } from 'react-popper'

import cx from 'classnames'

import { Box, Flex, IconButton, Text, Tooltip } from '@driftt/dds'
import { faEllipsis } from '@driftt/dds.icons/light/faEllipsis'
import { access } from '@driftt/traffic-control'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import DeleteFilterModal from 'components/DeleteFilterModal'
import Portal from 'components/Portal'
import { CurrentUserContext } from 'context/currentUserContext'
import { ICPSettingsContext } from 'context/icpSettingsContext'
import useOnClickOutside from 'hooks/useOnClickOutside'
import mergeRefs from 'utils/mergeRefs'
import { segmentTrack, TrackType } from 'utils/tracking'

import { ReactComponent as Email } from '../../svgs/activities/Email-2.svg'
import { ReactComponent as SiteVisit } from '../../svgs/activities/SiteVisit-2.svg'

import styles from './styles.module.css'

interface SavedFiltersActionMenuProps {
  isActive: boolean
  savedFilter: DeserializedSavedFilter
  defaultFilterId?: number
  onOpenEditModal: (filter: DeserializedSavedFilter) => void
  removeSavedFilter: (id: number) => void
  updateDefaultFilterId: (id: number) => void
  setActiveFilterId: (id: number) => void
  includeUpdateDefaultFilter?: boolean
  showToolTip?: boolean
}

const SavedFiltersActionMenu = ({
  isActive,
  savedFilter,
  defaultFilterId,
  onOpenEditModal,
  removeSavedFilter,
  updateDefaultFilterId,
  setActiveFilterId,
  includeUpdateDefaultFilter = true,
  showToolTip = false
}: SavedFiltersActionMenuProps) => {
  const observed = useRef(null)
  const currentUser = useContext(CurrentUserContext)
  const hasSavedViewEmailDigest = access.hasGates('account_activity_email_digest')
  const hasSavedViewNotifications = access.hasGates('saved_filters_push_notifications')
  const hasCustomAccountActivityNotifs = hasSavedViewEmailDigest || hasSavedViewNotifications
  const isAdmin = access.isAdmin()

  const [isPopoutOpen, setIsPopoutOpen] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [preventOnClick, setPreventOnClick] = useState(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const boundaryElement = useRef<HTMLDivElement>(null)
  const referenceElement = useRef<HTMLDivElement>(null)

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const { styles: popperStyles, attributes } = usePopper(referenceElement?.current, popperElement, {
    placement: 'right',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: boundaryElement?.current ?? 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false
        }
      }
    ]
  })

  const togglePopoutMenu = (event: any) => {
    event.stopPropagation()
    if (preventOnClick) {
      setPreventOnClick(false)
      return
    }
    setIsPopoutOpen(!isPopoutOpen)
  }

  const changeDefaultFilter = () => {
    updateDefaultFilterId(savedFilter.id)
  }

  const openDeleteModal = () => {
    setDeleteModalOpen(true)
  }
  const { setShowPreferencesModal } = useContext(ICPSettingsContext)

  const handleManageNotificationsClick = () => {
    segmentTrack(TrackType.MANAGE_NOTIFICATIONS_BUTTON_CLICKED)
    setShowPreferencesModal(true)
  }

  useOnClickOutside(observed, togglePopoutMenu)

  const filterCreatedByUser = savedFilter.ownerId === currentUser?.id

  return (
    <Flex
      ref={boundaryElement}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false)
        setIsPopoutOpen(false)
      }}
      onClick={() => {
        segmentTrack(TrackType.SAVED_FILTER_SELECTED)
        setActiveFilterId(savedFilter.id)
      }}
      alignItems="center"
      justifyContent="space-between"
      css={{
        backgroundColor: isActive ? '$gray2' : '$white',
        padding: '$1',
        borderRadius: '$1',
        cursor: 'pointer',
        margin: '5px 0',
        '&:hover': {
          backgroundColor: '$gray2'
        }
      }}>
      {isDeleteModalOpen && (
        <DeleteFilterModal
          savedFilter={savedFilter}
          isModalOpen={isDeleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onRemoveSavedFilter={removeSavedFilter}
        />
      )}
      <Box
        fullWidth
        css={{
          overflow: 'hidden'
        }}>
        <Flex alignItems="center" justifyContent="space-between" fullWidth>
          <Tooltip
            className={cx(styles.SavedFilterToolTip, {
              [styles.HideSavedFilterTooltip]: isPopoutOpen || !showToolTip
            })}
            content={savedFilter.name}
            side="right">
            <Flex
              alignItems="center"
              fullWidth
              justifyContent="space-between"
              css={{
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
              <Box
                css={{
                  width: '140px',
                  overflow: 'hidden',
                  textAlign: 'left'
                }}>
                <Text variant="body" truncate>
                  {savedFilter.name}
                </Text>
              </Box>

              {(includeUpdateDefaultFilter ||
                filterCreatedByUser ||
                hasCustomAccountActivityNotifs) && (
                <Box
                  ref={referenceElement}
                  css={{
                    position: 'relative'
                  }}>
                  {isHovering && (
                    <IconButton
                      css={{
                        flexBasis: '20px',
                        maxHeight: '20px',
                        '&:hover': {
                          backgroundColor: '$gray3'
                        }
                      }}
                      icon={faEllipsis}
                      variant="tertiary"
                      size="small"
                      title=""
                      hideTooltip
                      onClick={togglePopoutMenu}
                    />
                  )}

                  <Box
                    ref={setPopperElement}
                    style={popperStyles.popper}
                    className={cx({
                      [styles.PopperMenu]: isPopoutOpen
                    })}>
                    {isPopoutOpen && (
                      <Portal>
                        <Box
                          as="ul"
                          css={{
                            zIndex: 1000,
                            overflow: 'visible',
                            '> li': {
                              cursor: 'pointer'
                            }
                          }}
                          style={popperStyles.popper}
                          {...attributes.popper}
                          ref={mergeRefs([observed, setPopperElement])}
                          className={styles.ActionMenuPopout}>
                          {filterCreatedByUser && (
                            <li
                              className={styles.ActionMenuPopoutItem}
                              key="action-edit-filter"
                              onClick={() => onOpenEditModal(savedFilter)}>
                              Edit saved filter
                            </li>
                          )}
                          {includeUpdateDefaultFilter && (
                            <li
                              className={cx(styles.ActionMenuPopoutItem, {
                                [styles.PopoutItemDisabled]: defaultFilterId === savedFilter.id
                              })}
                              key="action-set-default-filter"
                              onClick={changeDefaultFilter}>
                              Set as my default saved filter
                            </li>
                          )}

                          {hasCustomAccountActivityNotifs && (
                            <li
                              className={styles.ActionMenuPopoutItem}
                              key="manage-filter-notifications"
                              onClickCapture={() => handleManageNotificationsClick()}>
                              <span>Manage notifications</span>
                              <div>
                                {hasSavedViewNotifications && (
                                  <SiteVisit
                                    className={cx(
                                      styles.ActionMenuPopoutIcon,
                                      styles.SiteVisitIcon,
                                      {
                                        [styles.ActivePopoutIcon]:
                                          savedFilter?.hasDesktop || savedFilter?.hasMobile
                                      }
                                    )}
                                  />
                                )}
                                <Email
                                  className={cx(styles.ActionMenuPopoutIcon, styles.EmailIcon, {
                                    [styles.ActivePopoutIcon]: savedFilter?.hasEmail
                                  })}
                                />
                              </div>
                            </li>
                          )}

                          {(filterCreatedByUser || isAdmin) && (
                            <li
                              className={cx(styles.ActionMenuPopoutItem, styles.PopoutItemWarning)}
                              key="action-delete-filter"
                              onClick={openDeleteModal}>
                              Delete saved filter
                            </li>
                          )}
                        </Box>
                      </Portal>
                    )}
                  </Box>
                </Box>
              )}
            </Flex>
          </Tooltip>
        </Flex>
      </Box>
    </Flex>
  )
}

export default SavedFiltersActionMenu
